import React, { useEffect, useState } from "react";
import { Heart, ChatCircle, Plus, Users, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

function CommentBox({ post, onComment }) {
  const [text, setText] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await onComment(post.post_id, text);
    setText("");
  };
  return (
    <form onSubmit={submit} className="flex gap-2 mt-3" data-testid={`comment-form-${post.post_id}`}>
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Drop a comment..." className="bg-surface-elevated border-white/10 text-sm" data-testid={`comment-input-${post.post_id}`} />
      <button type="submit" className="border border-white/10 hover:border-brand-accent px-3 transition-colors" data-testid={`comment-submit-${post.post_id}`}>
        <PaperPlaneTilt size={16} className="text-brand-accent" />
      </button>
    </form>
  );
}

export default function Social() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [feedFilter, setFeedFilter] = useState("all"); // "all" | "following"

  const load = () => {
    const endpoint = feedFilter === "following" ? "/social/feed" : "/social/posts";
    return api.get(endpoint).then(({ data }) => setPosts(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [feedFilter]);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setPosting(true);
    try {
      await api.post("/social/posts", { content });
      toast.success("Posted to the feed.");
      setContent("");
      load();
    } catch {
      toast.error("Failed");
    } finally {
      setPosting(false);
    }
  };

  const toggleLike = async (id) => {
    const { data } = await api.post(`/social/posts/${id}/like`);
    setPosts((ps) => ps.map((p) => p.post_id === id ? { ...p, likes: data.likes } : p));
  };

  const addComment = async (id, text) => {
    const { data } = await api.post(`/social/posts/${id}/comments`, { content: text });
    setPosts((ps) => ps.map((p) => p.post_id === id ? { ...p, comments: [...(p.comments || []), data] } : p));
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="social-page">
      <div className="mb-8">
        <div className="label-tiny text-brand-accent mb-2">// COMMUNITY</div>
        <h1 className="font-heading text-4xl sm:text-5xl mb-3">SOCIAL FEED</h1>
        <p className="text-text-secondary text-sm">Share your PRs. Cheer your squad. Stay accountable.</p>
        <div className="flex gap-1 mt-4 border-b border-white/10">
          <button
            onClick={() => setFeedFilter("all")}
            className={`px-4 py-2 text-xs label-tiny tracking-wider border-b-2 transition-colors ${feedFilter === "all" ? "border-brand-accent text-brand-accent" : "border-transparent text-text-secondary hover:text-white"}`}
            data-testid="feed-filter-all"
          >
            ALL ATHLETES
          </button>
          <button
            onClick={() => setFeedFilter("following")}
            className={`px-4 py-2 text-xs label-tiny tracking-wider border-b-2 transition-colors ${feedFilter === "following" ? "border-brand-accent text-brand-accent" : "border-transparent text-text-secondary hover:text-white"}`}
            data-testid="feed-filter-following"
          >
            FOLLOWING
          </button>
        </div>
      </div>

      <form onSubmit={submit} className="tactical-card p-5 mb-6" data-testid="post-form">
        <div className="flex items-start gap-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-brand-primary flex items-center justify-center text-sm font-bold flex-shrink-0">
              {user?.name?.[0]?.toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Hit a PR? Share the win..."
              className="bg-surface-elevated border-white/10 min-h-[80px] mb-3"
              data-testid="post-content-input"
            />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={posting || !content.trim()}
                className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50 flex items-center gap-2"
                data-testid="post-submit-button"
              >
                <Plus size={12} weight="bold" /> POST
              </button>
            </div>
          </div>
        </div>
      </form>

      {posts.length === 0 ? (
        <div className="tactical-card p-12 text-center" data-testid="social-empty">
          <Users size={36} weight="duotone" className="text-brand-primary mx-auto mb-4" />
          <h3 className="font-heading text-xl mb-2">FEED IS QUIET</h3>
          <p className="text-sm text-text-secondary">Be the first to share something with the community.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="posts-list">
          {posts.map((p) => {
            const liked = p.likes?.includes(user?.user_id);
            const showComments = expanded[p.post_id];
            return (
              <div key={p.post_id} className="tactical-card p-5" data-testid={`post-card-${p.post_id}`}>
                <div className="flex items-start gap-3 mb-3">
                  {p.user_picture ? (
                    <img src={p.user_picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-brand-primary flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {p.user_name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-base">{p.user_name?.toUpperCase()}</span>
                      <span className="label-tiny text-text-secondary">{new Date(p.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm mt-2 whitespace-pre-wrap">{p.content}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 pt-3 border-t border-white/5">
                  <button
                    onClick={() => toggleLike(p.post_id)}
                    className={`flex items-center gap-1.5 text-xs label-tiny transition-colors ${liked ? "text-brand-accent" : "text-text-secondary hover:text-white"}`}
                    data-testid={`like-button-${p.post_id}`}
                  >
                    <Heart size={16} weight={liked ? "fill" : "regular"} />
                    <span className="tabular-nums">{p.likes?.length || 0}</span>
                  </button>
                  <button
                    onClick={() => setExpanded({ ...expanded, [p.post_id]: !showComments })}
                    className="flex items-center gap-1.5 text-xs label-tiny text-text-secondary hover:text-white"
                    data-testid={`comment-toggle-${p.post_id}`}
                  >
                    <ChatCircle size={16} />
                    <span className="tabular-nums">{p.comments?.length || 0}</span>
                  </button>
                </div>
                {showComments && (
                  <div className="mt-3 pt-3 border-t border-white/5" data-testid={`comments-${p.post_id}`}>
                    {p.comments?.map((c) => (
                      <div key={c.comment_id} className="flex gap-2 py-2 text-sm">
                        <span className="font-heading">{c.user_name?.toUpperCase()}:</span>
                        <span className="text-text-secondary">{c.content}</span>
                      </div>
                    ))}
                    <CommentBox post={p} onComment={addComment} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
